import 'bulma/css/bulma.min.css';
import '@creativebulma/bulma-tooltip/dist/bulma-tooltip.min.css';
import '../index.css' // Has to be imported after bulma to override
import { useState, useEffect } from "react";
import { Form, Button, Heading, Box, Navbar } from "react-bulma-components";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../js/hooks/useAuth';
import { signOutUser } from '../js/firebase/config';
import httpRequest from '../js/utils/httpRequest';
import generateUserAlias from '../js/utils/UserAliasGenerator';
import LastJoinedChannels from '../components/home/LastJoinedChannels';
import styles from '../styles/box.module.css';
import holorayLogo from '../assets/images/png/logo.png';
import bgMain from '../assets/images/bg.jpg';

export default function Home() {
    const [userAlias, setUserAlias] = useState("");
    const [channelId, setChannelId] = useState("");
    const { user } = useAuth();

    let navigate = useNavigate();

    useEffect(() => {
        setUserAlias(localStorage.getItem('userAlias') || generateUserAlias());
    }, []);
    
    const handleLogout = async () => {
        try {
            await signOutUser();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    function onJoinChannelClick(id = channelId) {
        if (!id) {
            return;
        }

        localStorage.setItem('userAlias', userAlias);

        navigate(`/channel/${id}`);
    }

    function onCreateChannelClick() {
        localStorage.setItem('userAlias', userAlias);

        httpRequest.getNewChannel(id => {
            navigate(`/channel/${id}`);
        });
    }

    return (
        <div className="home-container">
            {/* Header with user info and logout */}
            <div className="home-header">
                <div className="user-info">
                    <span className="user-name">{user?.displayName || user?.email}</span>
                </div>
                <Button 
                    className="logout-button" 
                    onClick={handleLogout}
                    size="small"
                >
                    <FontAwesomeIcon icon={faSignOutAlt} />
                    <span>Logout</span>
                </Button>
            </div>

            <div className="home-background" style={{ backgroundImage: `url(${bgMain})` }}>
                <Box className={`${styles.box} home-box`}>
                    <form className="home-form">
                        <div className="logo-container">
                            <img
                                alt="HOLORAY"
                                src={holorayLogo}
                                className="logo-image"
                            />
                        </div>
                        <Form.Field>
                            <Form.Label className="form-label">Your Name</Form.Label>
                            <Form.Control>
                                <Form.Input 
                                    type="text" 
                                    placeholder="Type your name here" 
                                    onChange={({ target }) => setUserAlias(target.value)} 
                                    value={userAlias}
                                    className="form-input"
                                />
                            </Form.Control>
                        </Form.Field>
                        <Form.Field>
                            <Form.Label className="form-label">Channel ID</Form.Label>
                            <Form.Control>
                                <Form.Input 
                                    className={`form-input ${!channelId ? "is-danger" : ""}`} 
                                    type="text" 
                                    placeholder="Enter a channel ID" 
                                    onChange={({ target }) => setChannelId(target.value)}
                                />
                            </Form.Control>
                        </Form.Field>
                        <Button 
                            className="join-button" 
                            fullwidth 
                            color="link" 
                            onClick={() => onJoinChannelClick()}
                        >
                            Join
                        </Button>
                    </form>
                </Box>
            </div>

            <style>{`
                .home-container {
                    min-height: 100vh;
                    width: 100%;
                }

                .home-header {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    z-index: 1000;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 2rem;
                    background: rgba(0, 0, 0, 0.1);
                    backdrop-filter: blur(10px);
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                }

                .user-info {
                    color: white;
                    font-weight: 500;
                }

                .user-name {
                    font-size: 0.9rem;
                }

                .logout-button {
                    background: rgba(255, 255, 255, 0.1) !important;
                    border: 1px solid rgba(255, 255, 255, 0.2) !important;
                    color: white !important;
                    font-size: 0.8rem !important;
                    padding: 0.5rem 1rem !important;
                    border-radius: 6px !important;
                    transition: all 0.2s ease !important;
                }

                .logout-button:hover {
                    background: rgba(255, 255, 255, 0.2) !important;
                    transform: translateY(-1px);
                }

                .logout-button span {
                    margin-left: 0.5rem;
                }

                .home-background {
                    min-height: 100vh;
                    background-size: cover;
                    background-position: center;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 1.5rem;
                    position: relative;
                    padding-top: 5rem;
                }

                .home-background::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.3);
                    backdrop-filter: blur(5px);
                }

                .home-box {
                    width: 100%;
                    max-width: 450px;
                    margin: 0 auto;
                    background: rgba(255, 255, 255, 0.15);
                    border-radius: 12px;
                    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                    backdrop-filter: blur(8px);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    position: relative;
                    z-index: 1;
                }

                .home-form {
                    padding: 1.25rem;
                }

                .logo-container {
                    text-align: center;
                    margin-bottom: -1rem;
                    padding: 0 0.5rem;
                }

                .logo-image {
                    width: 100%;
                    max-width: 400px;
                    height: auto;
                    filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
                }

                .form-label {
                    color: #ffffff;
                    font-weight: 600;
                    font-size: 0.9rem;
                    margin-bottom: 0.5rem;
                    display: block;
                    letter-spacing: 0.5px;
                }

                .form-input {
                    height: 2.75rem;
                    font-size: 0.95rem;
                    border-radius: 8px;
                    background: rgba(255, 255, 255, 0.75);
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    transition: all 0.2s ease;
                    margin-bottom: 1rem;
                    padding: 0 1rem;
                    color: #333;
                }

                .form-input:focus {
                    background: #ffffff;
                    border-color: rgba(255, 255, 255, 0.5);
                    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
                    outline: none;
                }

                .form-input::placeholder {
                    color: rgba(0, 0, 0, 0.4);
                }

                .join-button {
                    height: 2.75rem;
                    font-size: 1rem;
                    margin-top: 1.5rem;
                    border-radius: 8px;
                    background: rgba(255, 255, 255, 0.95);
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    color: #333;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }

                .join-button:hover {
                    background: #ffffff;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }

                @media (max-width: 768px) {
                    .home-background {
                        padding: 1rem;
                    }

                    .home-box {
                        padding: 1rem;
                    }

                    .home-form {
                        padding: 1.25rem;
                    }

                    .logo-container {
                        margin-bottom: -1rem;
                    }

                    .logo-image {
                        max-width: 400px;
                    }

                    .form-input {
                        height: 2.5rem;
                        margin-bottom: 0.75rem;
                    }

                    .join-button {
                        height: 2.5rem;
                        margin-top: 1.25rem;
                    }
                }

                @media (max-width: 480px) {
                    .home-form {
                        padding: 1.25rem;
                    }

                    .logo-container {
                        margin-bottom: -1rem;
                    }

                    .logo-image {
                        max-width: 400px;
                    }
                }
            `}</style>
        </div>
    );
}